// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { WorkCellTask } from '@evolvemep/api-ts';
import { Flex } from '@mantine/core';
import type { LinkProps } from 'react-router';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { AgGridStyleTooltip } from 'components/Mantine/AgGridStyleTooltip';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import type { PageTitle } from 'components/Mantine/Navigation/types';
import type { StrongOmit, WithOptional } from 'helpers/helper-types';

export const WorkCellTaskLink = ({
  task,
  linkProps,
  children,
  from = 'Tasks',
}: {
  task: WithOptional<
    Pick<WorkCellTask, 'taskId' | 'taskName' | 'hasIncompletePredecessors'>,
    'hasIncompletePredecessors'
  >;
  children?: ReactNode;
  from?: PageTitle;
  linkProps?: Partial<StrongOmit<LinkProps, 'state'>>;
}) => (
  <AgGridStyleTooltip
    hidden={!task.hasIncompletePredecessors}
    label="This task has an incomplete predecessor."
    withArrow
    openDelay={150}
    position="top-start"
  >
    <EvolveLink from={from} {...linkProps} to={`/task-viewer/${task.taskId}`}>
      {children ?? (
        <Flex align="center" gap="xs" fz="sm">
          <Flex align="center" justify="center" w={20}>
            <EvolveIcon
              icon={task.hasIncompletePredecessors ? 'Blocked' : 'WorkCellTask'}
              size={task.hasIncompletePredecessors ? 'sm' : 'md'}
              light
            />
          </Flex>
          {task.taskName}
        </Flex>
      )}
    </EvolveLink>
  </AgGridStyleTooltip>
);
